import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./loaders.css";
import "./assets/fonts/print-emoji/print-emoji.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "react-toastify/dist/ReactToastify.css";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { BrowserRouter } from "react-router-dom";
import { clearEtsyMarkers } from "./services/etsyService";

// ========================================
// EXTRACT COOKIES BEFORE REACT RENDERS
// ========================================
(function initializeFromCookies() {
  console.log("All JS-visible cookies:", document.cookie);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const clearCookie = (cookieName) => {
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;

    if (process.env.REACT_APP_COOKIE_URL) {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${process.env.REACT_APP_COOKIE_URL};`;
    }

    console.log(`${cookieName} cookie cleared`);
  };

  const normalizeToArray = (key) => {
    const existing = localStorage.getItem(key);
    if (!existing) return [];

    try {
      const parsed = JSON.parse(existing);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") {
        const arr = [parsed];
        localStorage.setItem(key, JSON.stringify(arr));
        return arr;
      }
      localStorage.removeItem(key);
      return [];
    } catch {
      localStorage.removeItem(key);
      return [];
    }
  };

  const getVariantId = (plan) =>
    plan?.shopifyVariantId || plan?.variantId || plan?.id || null;

  // ========================================
  // CLEAN DUPLICATES (assigned beats unassigned)
  // ========================================
  const cleanupDuplicatePlans = () => {
    const selectedPlans = normalizeToArray("selectedPlan");
    const unassignedPlans = normalizeToArray("unassignedPlans");

    if (!selectedPlans.length || !unassignedPlans.length) return;

    const assignedVariantIds = new Set(
      selectedPlans
        .filter((p) => p.bookId)
        .map(getVariantId)
        .filter(Boolean)
    );

    const cleaned = unassignedPlans.filter(
      (p) => !assignedVariantIds.has(getVariantId(p))
    );

    if (cleaned.length !== unassignedPlans.length) {
      localStorage.setItem("unassignedPlans", JSON.stringify(cleaned));
    }
  };

  // ========================================
  // MERGE UNASSIGNED PLAN COOKIE
  // ========================================
  const mergeUnassignedPlanFromCookie = () => {
    const cookie = getCookie("unassignedPlan");
    if (!cookie) return;

    let cookieData;
    try {
      cookieData = JSON.parse(decodeURIComponent(cookie));
    } catch {
      clearCookie("unassignedPlan");
      return;
    }

    const plans = Array.isArray(cookieData) ? cookieData : [cookieData];
    let unassignedPlans = normalizeToArray("unassignedPlans");

    plans.forEach((cookiePlan) => {
      const variantId = getVariantId(cookiePlan);
      if (!variantId) return;

      const hasQty =
        cookiePlan.quantity !== undefined && cookiePlan.quantity !== null;
      const qty = hasQty ? cookiePlan.quantity : 1;

      let matched = false;

      unassignedPlans = unassignedPlans.map((item) => {
        if (getVariantId(item) !== variantId) return item;

        matched = true;
        return {
          ...item,
          ...cookiePlan,
          quantity: hasQty ? qty : item.quantity || 1,
          planPricingData: {
            ...(item.planPricingData || {}),
            ...(cookiePlan.planPricingData || {}),
            quantity: hasQty ? qty : item.quantity || 1,
          },
        };
      });

      if (!matched) {
        unassignedPlans.push({
          ...cookiePlan,
          quantity: qty,
          planPricingData: {
            ...(cookiePlan.planPricingData || {}),
            quantity: qty,
          },
        });
      }
    });

    localStorage.setItem("unassignedPlans", JSON.stringify(unassignedPlans));
    clearCookie("unassignedPlan");
  };

  // ========================================
  // MERGE SELECTED PLAN COOKIE
  // ========================================
  const mergeSelectedPlanFromCookie = () => {
    const cookie = getCookie("selectedPlan");
    if (!cookie) return;

    let cookieData;
    try {
      cookieData = JSON.parse(decodeURIComponent(cookie));
    } catch {
      clearCookie("selectedPlan");
      return;
    }

    const plans = Array.isArray(cookieData) ? cookieData : [cookieData];

    let selectedPlans = normalizeToArray("selectedPlan");
    let unassignedPlans = normalizeToArray("unassignedPlans");

    plans.forEach((cookiePlan) => {
      const variantId = getVariantId(cookiePlan);
      if (!variantId) return;

      const hasQty =
        cookiePlan.quantity !== undefined && cookiePlan.quantity !== null;
      const qty = hasQty ? cookiePlan.quantity : 1;

      let foundInSelected = false;

      selectedPlans = selectedPlans.map((item) => {
        if (getVariantId(item) !== variantId) return item;

        foundInSelected = true;
        return {
          ...item,
          ...cookiePlan,
          quantity: hasQty ? qty : item.quantity || 1,
          planPricingData: {
            ...(item.planPricingData || {}),
            ...(cookiePlan.planPricingData || {}),
            quantity: hasQty ? qty : item.quantity || 1,
          },
        };
      });

      if (!foundInSelected) {
        let foundInUnassigned = false;

        unassignedPlans = unassignedPlans.map((item) => {
          if (getVariantId(item) !== variantId) return item;

          foundInUnassigned = true;
          return {
            ...item,
            ...cookiePlan,
            quantity: hasQty ? qty : item.quantity || 1,
            planPricingData: {
              ...(item.planPricingData || {}),
              ...(cookiePlan.planPricingData || {}),
              quantity: hasQty ? qty : item.quantity || 1,
            },
          };
        });

        if (!foundInUnassigned) {
          unassignedPlans.push({
            ...cookiePlan,
            quantity: qty,
            planPricingData: {
              ...(cookiePlan.planPricingData || {}),
              quantity: qty,
            },
          });
        }
      }
    });

    localStorage.setItem("selectedPlan", JSON.stringify(selectedPlans));
    localStorage.setItem("unassignedPlans", JSON.stringify(unassignedPlans));

    clearCookie("selectedPlan");
  };

  // ========================================
  // AUTH / CUSTOMER
  // ========================================
  const authToken = getCookie("auth_token");
  if (authToken) {
    // A session arriving by cookie is the Shopify handoff — Etsy buyers get
    // their token from the /etsy page directly and never via this cookie. Any
    // Etsy markers still in storage are leftovers from someone else on this
    // browser; left in place they would swap this customer's checkout button
    // for "send to print". Clear them before the session starts.
    clearEtsyMarkers();
    localStorage.setItem("auth_token", authToken);
  }

  const shopifyCustomerId = getCookie("shopifyCustomerId");
  if (shopifyCustomerId) {
    try {
      localStorage.setItem(
        "shopifyCustomerId",
        decodeURIComponent(shopifyCustomerId)
      );
    } catch { }
  }

  // FLOW
  normalizeToArray("selectedPlan");
  normalizeToArray("unassignedPlans");

  let didMergeSelected = false;
  let didMergeUnassigned = false;

  if (getCookie("selectedPlan")) {
    mergeSelectedPlanFromCookie();
    didMergeSelected = true;
  }

  if (getCookie("unassignedPlan")) {
    mergeUnassignedPlanFromCookie();
    didMergeUnassigned = true;
  }

  cleanupDuplicatePlans();

  // If we merged plans from cookies and we have a token, sync them to the database asynchronously
  if ((didMergeSelected || didMergeUnassigned) && getCookie("auth_token")) {
    window.__DID_COOKIE_MERGE__ = true;
    import('./services/shopifyCartService').then(({ syncLocalCartToDB }) => {
      console.log("Triggering database sync from recovered cookies...");
      syncLocalCartToDB().catch(err => console.error("Cookie sync error:", err));
    });
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();